import { useMutation } from "@tanstack/react-query";
import { Check, X } from "lucide-react";
import { useState } from "react";
import { useI18n } from "../i18n/I18nProvider";
import { Button } from "../components/ui/Button";
import { ErrorText } from "../components/ui/feedback";
import type { Configuration } from "../lib/api-types";
import { adminApi } from "./api";
import type { ValidationResult } from "./types";

export interface ConfigurationValidationProps {
  workspaceId: string;
  configuration: Configuration;
}

const ICON = "mt-0.5 size-[18px] shrink-0";

/** One configuration row. Validate lists the result as a checklist: blue check when clean, red cross per issue. */
export function ConfigurationValidation({
  workspaceId,
  configuration,
}: ConfigurationValidationProps) {
  const { t } = useI18n();
  const [result, setResult] = useState<ValidationResult | null>(null);

  const validate = useMutation({
    mutationFn: () => adminApi.validateConfiguration(workspaceId, configuration.id),
    onSuccess: (data) => setResult(data),
  });

  const issues = result?.issues ?? [];

  return (
    <li className="space-y-2 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-display text-lg leading-tight font-bold tracking-[0.01em] uppercase">
          {configuration.name}
        </span>
        <Button size="sm" disabled={validate.isPending} onClick={() => validate.mutate()}>
          {t("admin.action.validate")}
        </Button>
      </div>
      <ErrorText error={validate.error} />
      {result ? (
        <ul className="space-y-1.5">
          {issues.length === 0 ? (
            <li className="flex items-start gap-2 text-operate text-ink">
              <Check aria-hidden="true" className={`${ICON} text-blue`} strokeWidth={1.75} />
              {t("admin.validate.noIssues")}
            </li>
          ) : (
            <li className="text-note font-semibold text-red">
              {t("admin.validate.count", { count: issues.length })}
            </li>
          )}
          {issues.map((issue, index) => (
            <li
              key={`${issue.code}-${index}`}
              className="flex items-start gap-2 text-operate text-ink"
            >
              <X aria-hidden="true" className={`${ICON} text-red`} strokeWidth={1.75} />
              <span className="min-w-0">
                <span className="block text-micro tabular-nums text-ink-3">{issue.code}</span>
                <span>{issue.detail}</span>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}
